import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Poll } from '@/constants/types';
import { feedService } from '@/services/feed/feedService';
import toast from 'react-hot-toast';

interface PollCardProps {
  poll: Poll;
  feedId: string;
  userHasVoted?: boolean;
  userVoteIndex?: number;
  onVoteSuccess?: () => void;
}

export const PollCard: React.FC<PollCardProps> = ({
  poll,
  feedId,
  userHasVoted: initialHasVoted,
  userVoteIndex: initialVoteIndex,
  onVoteSuccess
}) => {
  const theme = useTheme();
  const [isVoting, setIsVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(initialHasVoted);
  const [voteIndex, setVoteIndex] = useState(initialVoteIndex);
  const [currentPoll, setCurrentPoll] = useState(poll);

  // Sync state when props change (e.g., background hover update)
  useEffect(() => {
    setHasVoted(initialHasVoted);
    setVoteIndex(initialVoteIndex);
    setCurrentPoll(poll);
  }, [initialHasVoted, initialVoteIndex, poll]);

  const totalVotes = currentPoll.options.reduce((sum, opt) => sum + (opt.votes || 0), 0);

  const handleVote = async (optionIndex: number) => {
    if (hasVoted || isVoting) return;

    try {
      setIsVoting(true);

      // 1. Cast the vote
      await feedService.votePoll(feedId, optionIndex);
      toast.success('Vote cast successfully');

      // Optimistic update so UI reacts immediately
      const newOptions = [...currentPoll.options];
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        votes: (newOptions[optionIndex].votes || 0) + 1,
      };
      setCurrentPoll({ ...currentPoll, options: newOptions });
      setHasVoted(true);
      setVoteIndex(optionIndex);

      // 2. Refetch the feed so poll counts/state reflect server truth
      try {
        const detailsResponse = await feedService.getFeedDetails(feedId);
        const updatedFeed: any = (detailsResponse as any)?.data?.feed ?? (detailsResponse as any)?.feed;
        if (updatedFeed?.poll) {
          setCurrentPoll(updatedFeed.poll);
          setHasVoted(updatedFeed.userHasVoted ?? true);
          setVoteIndex(updatedFeed.userVoteIndex ?? optionIndex);
        }
      } catch (refreshError) {
        console.error('Failed to refresh feed after vote:', refreshError);
      }

      if (onVoteSuccess) {
        onVoteSuccess();
      }
    } catch (error: any) {
      console.error('Error voting:', error);
      toast.error(error.response?.data?.message || 'Failed to cast vote');
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }} onClick={(e) => e.stopPropagation()}>
      {/* <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {currentPoll.question}
      </Typography> */}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {currentPoll.options.map((option, index) => {
          const percentage = totalVotes > 0 ? Math.round(((option.votes || 0) / totalVotes) * 100) : 0;
          const isSelected = hasVoted && voteIndex === index;

          return (
            <Box key={index} sx={{ position: 'relative' }}>
              <Button
                fullWidth
                variant={isSelected ? "contained" : "outlined"}
                onClick={() => handleVote(index)}
                disabled={hasVoted || isVoting}
                sx={{
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  // Ensure solid color for selected option
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  // Force text colors since MUI's disabled styles can override inheritance
                  color: isSelected ? 'white !important' : 'text.primary',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: isSelected ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'action.hover'),
                    borderColor: isSelected ? 'primary.dark' : 'primary.main',
                    transform: (!hasVoted && !isVoting) ? 'translateY(-2px)' : 'none',
                    boxShadow: (!hasVoted && !isVoting) ? (theme.palette.mode === 'dark' ? '0 4px 8px rgba(0,0,0,0.4)' : '0 4px 8px rgba(0,0,0,0.1)') : 'none',
                  },
                  '&.Mui-disabled': {
                    // Set color with !important to overcome MUI's disabled text button styles
                    color: isSelected ? 'white !important' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary'),
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'primary.main' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'transparent'),
                    opacity: 1
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  zIndex: 1
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? 'white' : 'inherit'
                  }}
                >
                  {option.text}
                </Typography>
                
                {hasVoted && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: isSelected ? 'white' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary')
                    }}
                  >
                    {percentage}%
                  </Typography>
                )}

                {isVoting && !hasVoted && <CircularProgress size={20} sx={{ ml: 1, color: isSelected ? 'white' : 'primary.main' }} />}
              </Button>

              {hasVoted && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: `${percentage}%`,
                    bgcolor: isSelected ? 'primary.main' : 'primary.main',
                    opacity: isSelected ? 0.3 : 0.1,
                    borderRadius: 2,
                    transition: 'width 0.5s ease-in-out',
                    zIndex: 0
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', fontWeight: 500 }}>
        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'} • {hasVoted ? 'You have voted' : 'Cast your vote'}
      </Typography>
    </Box>
  );
};
